import React, { FC, useEffect, useMemo, useState } from 'react'
import { KTSVG } from '../../../../../_metronic/helpers'
import { useAuth } from '../../../auth'
import { getRunningHourReadingsPaged } from '../../core/pms/_requests'
import type { PagedResult, RunningHourCounterDto, RunningHourReadingDto } from '../../core/pms/_models'
import { EditRunningHourReadingModal } from './EditRunningHourReadingModal'
import { TECH_PERM } from '../../core/technicalPermissions'
import SearchableSelect from '../../../../shared/components/SearchableSelect'

interface Props {
  visible: boolean
  counter: RunningHourCounterDto
  onClose: () => void
  onUpdated: () => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export const RunningHoursHistoryModal: FC<Props> = ({ visible, counter, onClose, onUpdated }) => {
  const { hasPermission } = useAuth()
  const canEditReadings = hasPermission(TECH_PERM.UPDATE_RUNNING_HOURS)

  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)
  const [data, setData] = useState<PagedResult<RunningHourReadingDto> | null>(null)
  const [loading, setLoading] = useState(false)
  const [editingReading, setEditingReading] = useState<RunningHourReadingDto | null>(null)
  const [refreshToken, setRefreshToken] = useState(0)

  useEffect(() => {
    if (visible) {
      setPage(0)
    }
  }, [visible, counter?.id])

  useEffect(() => {
    if (!visible) return
    const load = async () => {
      setLoading(true)
      try {
        const res = await getRunningHourReadingsPaged(counter.id!, page, size)
        setData(res)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [visible, counter?.id, page, size, refreshToken])

  const rows = useMemo(() => {
    const raw = data?.content ?? []
    const seen = new Set<number>()
    return raw.filter((r) => {
      if (r.value == null) return false
      if (seen.has(r.value)) return false
      seen.add(r.value)
      return true
    })
  }, [data?.content])
  const totalElements = data?.totalElements ?? 0
  const totalPages = data?.totalPages ?? 1
  const pageLabel = useMemo(() => {
    if (!totalElements) return '0'
    const start = page * size + 1
    const end = Math.min((page + 1) * size, totalElements)
    return `${start}-${end} of ${totalElements}`
  }, [page, size, totalElements])

  if (!visible) return null

  return (
    <div style={WRAP_STYLE} tabIndex={-1} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{ width: '820px', maxWidth: '95vw', maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3 flex-shrink-0'>
          <div>
            <h5 className='modal-title text-dark m-0'>Reading Update History</h5>
            <div className='text-muted fs-7 mt-1'>
              {counter.name} • {counter.equipmentName || 'Equipment'}
            </div>
          </div>
          <button type='button' className='btn-close' onClick={onClose}></button>
        </div>

        <div className='px-4 py-3 flex-grow-1 d-flex flex-column'>
          <div
            className='table-responsive border rounded'
            style={{ maxHeight: '55vh', overflowY: 'auto' }}
          >
            <table className='table table-bordered align-middle mb-0'>
              <thead className='table-header'>
                <tr>
                  <th style={{ width: '70px' }} className='text-center'>Sr/No</th>
                  <th style={{ minWidth: '160px' }}>Reading Date</th>
                  <th style={{ minWidth: '140px' }}>Reading Value</th>
                  <th style={{ minWidth: '140px' }}>Source</th>
                  {canEditReadings && <th style={{ width: '120px' }} className='text-center'>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={canEditReadings ? 5 : 4} className='text-center py-4'>
                      <div className='spinner-border' role='status'>
                        <span className='visually-hidden'>Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={canEditReadings ? 5 : 4} className='text-center py-4 text-muted'>
                      No readings found
                    </td>
                  </tr>
                ) : (
                  rows.map((reading, idx) => {
                    const isLatestReading = page === 0 && idx === 0
                    const canEditThisRow = canEditReadings && isLatestReading
                    return (
                    <tr key={reading.id ?? `${reading.readingDate}-${idx}`}>
                      <td className='text-center'>{page * size + idx + 1}</td>
                      <td>{new Date(reading.readingDate).toLocaleDateString()}</td>
                      <td className='text-end'>{reading.value.toLocaleString()}</td>
                      <td>{reading.source || '-'}</td>
                      {canEditReadings && (
                        <td className='text-center'>
                          {canEditThisRow && (
                            <button
                              className='btn btn-sm btn-light'
                              onClick={() => setEditingReading(reading)}
                              title='Edit latest reading'
                            >
                              <KTSVG path='/media/icons/duotune/general/gen055.svg' className='svg-icon-3' />
                            </button>
                          )}
                        </td>
                      )}
                    </tr>
                  )})
                )}
              </tbody>
            </table>
          </div>

          <div className='d-flex align-items-center justify-content-between pt-3'>
            <div className='d-flex align-items-center gap-2'>
              <span className='text-muted'>Rows</span>
              <SearchableSelect
                className='form-select form-select-sm'
                style={{ width: '80px' }}
                value={size}
                onChange={(e) => setSize(Number(e.target.value))}
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </SearchableSelect>
              <span className='text-muted'>{pageLabel}</span>
            </div>
            <div className='d-flex align-items-center gap-2'>
              <button
                className='btn btn-sm btn-light'
                disabled={page <= 0}
                onClick={() => setPage(p => Math.max(0, p - 1))}
              >
                Prev
              </button>
              <span className='text-muted'>
                Page {page + 1} / {totalPages}
              </span>
              <button
                className='btn btn-sm btn-light'
                disabled={page + 1 >= totalPages}
                onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {canEditReadings && editingReading && (
        <EditRunningHourReadingModal
          visible={!!editingReading}
          reading={editingReading}
          counter={counter}
          onClose={() => setEditingReading(null)}
          onSuccess={() => {
            setEditingReading(null)
            onUpdated()
            setRefreshToken(t => t + 1)
            setPage(0)
          }}
        />
      )}
    </div>
  )
}
